import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { QuickEnquiryForm } from '../components/QuickEnquiryForm';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Calendar, User, Tag, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const BlogPage = () => {
  const [blogs, setBlogs] = useState([]);
  const [isEnquiryOpen, setIsEnquiryOpen] = useState(false);

  useEffect(() => {
    fetchBlogs();
  }, []);

  const fetchBlogs = async () => {
    try {
      const response = await axios.get(`${API}/blogs`);
      setBlogs(response.data.blogs);
    } catch (error) {
      console.error('Error fetching blogs:', error);
    }
  };

  const truncateText = (text, maxLength) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-[#0A2463] via-[#0d2d6e] to-[#247BA0] relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-10 right-10 w-96 h-96 bg-[#FFB703] rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-float"></div>
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <Badge className="mb-6 bg-[#FFB703] text-white px-6 py-2">
              Our Blog
            </Badge>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              Business Insights & Guides
            </h1>
            <p className="text-xl text-gray-200">
              Expert articles on business registration, compliance, and growth strategies
            </p>
          </div>
        </div>
      </section>

      {/* Blog Listing */}
      <section className="py-20 bg-gradient-to-b from-white to-gray-50">
        <div className="container mx-auto px-4">
          <div className="space-y-12">
            {blogs.map((blog) => (
              <Card key={blog._id} className="border-none shadow-xl hover:shadow-2xl transition-all overflow-hidden">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-0">
                  {/* Blog Image */}
                  <div className="lg:col-span-1">
                    <img
                      src={blog.image_url}
                      alt={blog.title}
                      className="w-full h-64 lg:h-full object-cover"
                    />
                  </div>

                  {/* Blog Content */}
                  <div className="lg:col-span-2 p-8">
                    <div className="flex items-center gap-4 mb-4 text-sm text-gray-600">
                      <div className="flex items-center gap-2">
                        <Calendar size={16} />
                        <span>{new Date(blog.created_at).toLocaleDateString()}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <User size={16} />
                        <span>{blog.author}</span>
                      </div>
                    </div>

                    <h2 className="text-3xl font-bold text-[#0A2463] mb-3 hover:text-[#247BA0] transition-colors cursor-pointer">
                      <Link to={`/blog/${blog._id}`}>{blog.title}</Link>
                    </h2>
                    
                    <p className="text-lg text-gray-700 mb-4">{blog.subtitle}</p>
                    
                    <p className="text-gray-600 mb-6 leading-relaxed">
                      {truncateText(blog.description.replace(/[#*]/g, ''), 300)}
                    </p>

                    {/* Tags */}
                    <div className="flex items-center gap-2 mb-6 flex-wrap">
                      <Tag size={16} className="text-[#247BA0]" />
                      {blog.tags.split(',').slice(0, 5).map((tag, idx) => (
                        <Badge key={idx} variant="outline" className="text-xs">
                          {tag.trim()}
                        </Badge>
                      ))}
                    </div>

                    {/* Action Buttons */}
                    <div className="flex flex-wrap gap-4">
                      <Link to="/contact">
                        <Button className="bg-gradient-to-r from-[#0A2463] to-[#247BA0] hover:from-[#247BA0] hover:to-[#0A2463] text-white">
                          Apply Now
                          <ArrowRight className="ml-2" size={18} />
                        </Button>
                      </Link>
                      
                      <Button
                        onClick={() => setIsEnquiryOpen(true)}
                        variant="outline"
                        className="border-2 border-[#FFB703] text-[#0A2463] hover:bg-[#FFB703] hover:text-white"
                      >
                        Quick Enquiry
                      </Button>
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>

          {blogs.length === 0 && (
            <div className="text-center py-20">
              <p className="text-gray-500 text-xl">No blog posts available yet.</p>
            </div>
          )}
        </div>
      </section>

      <Footer />
      
      <QuickEnquiryForm 
        isOpen={isEnquiryOpen} 
        onClose={() => setIsEnquiryOpen(false)}
      />
    </div>
  );
};

export default BlogPage;
